/**
 * Panneau "Étudiants manquants / codes-barres illisibles" pour la vérification OCR devoirs.
 *
 * Deux cas :
 *   1. Étudiant attendu mais pas de ligne du tout → bouton "+ Ajouter la note"
 *   2. Ligne existante orpheline (image OK, matricule non lu) → bouton "Assigner"
 */
import React, { useState, useMemo } from 'react';
import {
  AlertTriangle, UserPlus, Search, X, Check, Loader2, Users, ScanBarcode,
} from 'lucide-react';
import {
  useGetEtudiantsManquantsDevoirsQuery,
  useAjouterLigneManuelleDevoirsMutation,
  useAssignerEtudiantLigneDevoirsMutation,
} from '../../services/ocrApi';

interface Props {
  releveId: number;
  disabled?: boolean;
  onLigneCreee?: () => void;
}

interface EtuManquant {
  id: number; matricule: string; nom: string; prenoms: string; classe: string;
}
interface LigneOrpheline {
  id: number; numero_ligne: number; numero_page: number;
  nom_detecte: string; has_image: boolean;
  note_d1_detectee: number | null; note_d2_detectee: number | null;
}

export const PanneauEtudiantsManquants: React.FC<Props> = ({ releveId, disabled, onLigneCreee }) => {
  const { data, refetch, isFetching } = useGetEtudiantsManquantsDevoirsQuery(releveId);
  const [open, setOpen] = useState(false);
  const [modalAjout, setModalAjout] = useState<EtuManquant | null>(null);
  const [modalAssign, setModalAssign] = useState<LigneOrpheline | null>(null);

  const manquants = data?.manquants || [];
  const orphelines = data?.lignes_orphelines || [];
  const total = manquants.length + orphelines.length;

  if (total === 0 && !isFetching) return null;

  return (
    <>
      <div className={`border-b ${
        total > 0 ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                  : 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
      }`}>
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center gap-3 px-6 py-2.5 text-left hover:bg-black/5"
        >
          <AlertTriangle size={16} className={total > 0 ? 'text-amber-600' : 'text-emerald-600'} />
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900 dark:text-white">
              {total > 0
                ? `${total} étudiant(s) à corriger — code-barres non lu(s)`
                : 'Tous les étudiants attendus sont présents ✓'}
            </div>
            {total > 0 && (
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {manquants.length > 0 && `${manquants.length} étudiant(s) manquant(s) totalement`}
                {manquants.length > 0 && orphelines.length > 0 && ' · '}
                {orphelines.length > 0 && `${orphelines.length} ligne(s) orpheline(s) (image OK, matricule illisible)`}
              </div>
            )}
          </div>
          {data && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {data.total_presents}/{data.total_attendus} lus par l'OCR
            </div>
          )}
        </button>

        {open && total > 0 && (
          <div className="px-6 pb-3 space-y-3">
            {/* Lignes orphelines (image OK, matricule illisible) */}
            {orphelines.length > 0 && (
              <div>
                <div className="text-[11px] uppercase font-bold text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1.5">
                  <ScanBarcode size={11} />
                  Lignes orphelines — matricule non lu par l'OCR ({orphelines.length})
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {orphelines.map(l => (
                    <div key={l.id} className="p-2 bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 rounded text-xs">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-gray-500">L. {l.numero_ligne} · p.{l.numero_page}</span>
                        {l.nom_detecte && (
                          <span className="truncate flex-1 italic text-gray-600 dark:text-gray-400">
                            "{l.nom_detecte}"
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-1.5">
                        <span>D1: <b>{l.note_d1_detectee ?? '—'}</b></span>
                        <span>D2: <b>{l.note_d2_detectee ?? '—'}</b></span>
                      </div>
                      <button
                        onClick={() => setModalAssign(l)}
                        disabled={disabled}
                        className="w-full py-1 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded font-semibold flex items-center justify-center gap-1"
                      >
                        <UserPlus size={11} /> Assigner un matricule
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Étudiants totalement manquants */}
            {manquants.length > 0 && (
              <div>
                <div className="text-[11px] uppercase font-bold text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1.5">
                  <Users size={11} />
                  Étudiants attendus sans ligne détectée ({manquants.length})
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {manquants.map(e => (
                    <div key={e.id} className="p-2 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded text-xs flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-mono font-semibold text-gray-900 dark:text-white truncate">
                          {e.matricule}
                        </div>
                        <div className="text-gray-500 truncate">
                          {e.nom} {e.prenoms}
                        </div>
                      </div>
                      <button
                        onClick={() => setModalAjout(e)}
                        disabled={disabled}
                        className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold flex items-center gap-1 shrink-0"
                      >
                        <UserPlus size={11} /> Ajouter
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {modalAjout && (
        <ModalAjoutLigne
          releveId={releveId}
          etudiant={modalAjout}
          onClose={() => setModalAjout(null)}
          onSuccess={() => { setModalAjout(null); refetch(); onLigneCreee?.(); }}
        />
      )}
      {modalAssign && (
        <ModalAssignerLigne
          releveId={releveId}
          ligne={modalAssign}
          manquants={manquants}
          onClose={() => setModalAssign(null)}
          onSuccess={() => { setModalAssign(null); refetch(); onLigneCreee?.(); }}
        />
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal "Ajouter une ligne pour un étudiant manquant"
// ─────────────────────────────────────────────────────────────────────────────

function ModalAjoutLigne({ releveId, etudiant, onClose, onSuccess }: {
  releveId: number;
  etudiant: EtuManquant;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [noteD1, setNoteD1] = useState('');
  const [absentD1, setAbsentD1] = useState(false);
  const [noteD2, setNoteD2] = useState('');
  const [absentD2, setAbsentD2] = useState(false);
  const [erreur, setErreur] = useState('');
  const [ajouter, { isLoading }] = useAjouterLigneManuelleDevoirsMutation();

  const parseN = (s: string): number | null => {
    const t = s.trim().replace(',', '.');
    if (!t) return null;
    const n = parseFloat(t);
    return isNaN(n) ? NaN : n;
  };

  const submit = async () => {
    setErreur('');
    const n1 = absentD1 ? null : parseN(noteD1);
    const n2 = absentD2 ? null : parseN(noteD2);
    if (n1 !== null && (n1 as any).valueOf?.() !== undefined && isNaN(n1 as any)) {
      setErreur('Note D1 invalide'); return;
    }
    if (n2 !== null && isNaN(n2 as any)) {
      setErreur('Note D2 invalide'); return;
    }
    if (n1 !== null && (n1 < 0 || n1 > 20)) { setErreur('D1 hors [0;20]'); return; }
    if (n2 !== null && (n2 < 0 || n2 > 20)) { setErreur('D2 hors [0;20]'); return; }
    try {
      await ajouter({
        releveId,
        matricule: etudiant.matricule,
        note_d1: n1 as any, absent_d1: absentD1,
        note_d2: n2 as any, absent_d2: absentD2,
      }).unwrap();
      onSuccess();
    } catch (e: any) {
      setErreur(e?.data?.error || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md">
        <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <UserPlus size={16} /> Ajouter la note manuellement
            </h3>
            <div className="text-xs text-gray-500 mt-0.5">
              Code-barre non lu par l'OCR
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded">
            <div className="font-mono font-bold text-blue-900 dark:text-blue-300">{etudiant.matricule}</div>
            <div className="text-sm text-blue-800 dark:text-blue-200 mt-0.5">
              {etudiant.nom} {etudiant.prenoms}
            </div>
            {etudiant.classe && (
              <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">Classe : {etudiant.classe}</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Note D1</label>
              <div className="flex items-center gap-2">
                <input
                  type="text" inputMode="decimal"
                  value={noteD1} onChange={e => setNoteD1(e.target.value)}
                  disabled={absentD1}
                  placeholder="0-20"
                  className="flex-1 px-2 py-1.5 text-sm border dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white disabled:opacity-50"
                  autoFocus
                />
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={absentD1} onChange={e => setAbsentD1(e.target.checked)} />
                  <span>Abs.</span>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Note D2</label>
              <div className="flex items-center gap-2">
                <input
                  type="text" inputMode="decimal"
                  value={noteD2} onChange={e => setNoteD2(e.target.value)}
                  disabled={absentD2}
                  placeholder="0-20"
                  className="flex-1 px-2 py-1.5 text-sm border dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white disabled:opacity-50"
                />
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={absentD2} onChange={e => setAbsentD2(e.target.checked)} />
                  <span>Abs.</span>
                </label>
              </div>
            </div>
          </div>

          {erreur && (
            <div className="p-2 bg-red-50 border border-red-200 text-red-700 rounded text-xs">
              {erreur}
            </div>
          )}
        </div>
        <div className="p-3 border-t dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={isLoading}
            className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold flex items-center gap-1.5"
          >
            {isLoading ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal "Assigner un matricule à une ligne orpheline"
// ─────────────────────────────────────────────────────────────────────────────

function ModalAssignerLigne({ releveId, ligne, manquants, onClose, onSuccess }: {
  releveId: number;
  ligne: LigneOrpheline;
  manquants: EtuManquant[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [recherche, setRecherche] = useState('');
  const [erreur, setErreur] = useState('');
  const [assigner, { isLoading }] = useAssignerEtudiantLigneDevoirsMutation();

  const filtres = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return manquants.slice(0, 30);
    return manquants.filter(e =>
      e.matricule.toLowerCase().includes(q)
      || e.nom.toLowerCase().includes(q)
      || (e.prenoms || '').toLowerCase().includes(q)
    ).slice(0, 30);
  }, [recherche, manquants]);

  const submit = async (matricule: string) => {
    setErreur('');
    try {
      await assigner({ releveId, ligneId: ligne.id, matricule }).unwrap();
      onSuccess();
    } catch (e: any) {
      setErreur(e?.data?.error || 'Erreur');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-md flex flex-col max-h-[80vh]">
        <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ScanBarcode size={16} /> Assigner un matricule
            </h3>
            <div className="text-xs text-gray-500 mt-0.5">
              Ligne {ligne.numero_ligne} · page {ligne.numero_page}
              {ligne.nom_detecte && <> · nom OCR : "<span className="italic">{ligne.nom_detecte}</span>"</>}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 border-b dark:border-gray-700">
          <div className="relative">
            <Search size={13} className="absolute left-2 top-2.5 text-gray-400" />
            <input
              type="text" autoFocus
              value={recherche} onChange={e => setRecherche(e.target.value)}
              placeholder="Rechercher par matricule ou nom..."
              className="w-full pl-7 pr-2 py-1.5 text-sm border dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {filtres.length === 0 ? (
            <div className="text-center text-xs text-gray-500 py-6">Aucun étudiant.</div>
          ) : filtres.map(e => (
            <button
              key={e.id}
              onClick={() => submit(e.matricule)}
              disabled={isLoading}
              className="w-full text-left p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded flex items-center gap-2 text-sm"
            >
              <div className="flex-1 min-w-0">
                <div className="font-mono font-semibold text-gray-900 dark:text-white">{e.matricule}</div>
                <div className="text-xs text-gray-500 truncate">{e.nom} {e.prenoms}</div>
              </div>
              <UserPlus size={13} className="text-blue-600 shrink-0" />
            </button>
          ))}
        </div>

        {erreur && (
          <div className="p-3 bg-red-50 border-t border-red-200 text-red-700 text-xs">
            {erreur}
          </div>
        )}
      </div>
    </div>
  );
}
